import React, { useState, useEffect } from 'react';
import Pagination from '../../components/Pagination';
import ClearFilter from '../../components/ClearFilter';
import ExportTable from '../../components/ExportTable';
import './ApproveCertifications.css';
import { Eye, Download } from "lucide-react";
import { FaClock, FaUsers, FaCertificate, FaCheck, FaTimes } from "react-icons/fa";
import { apiFetch, apiFetchBlob } from '../../utils/api';

// Utility function for consistent date formatting
const formatDate = (dateString, fallback = 'N/A') => {
  if (!dateString || dateString === 'n/a' || dateString === 'N/A') return fallback;
  try {
    // Handle different date formats
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return fallback;

    // Format as DD/MM/YYYY for consistency
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (error) {
    return fallback;
  }
};

const ApproveCertificates = () => {
  const [originalCertificates, setOriginalCertificates] = useState([]);
  const [filteredCertificates, setFilteredCertificates] = useState([]);
  const [originalPendingCertificates, setOriginalPendingCertificates] = useState([]);
  const [filteredPendingCertificates, setFilteredPendingCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Separate pagination states
  const [currentPagePending, setCurrentPagePending] = useState(1);
  const [itemsPerPagePending, setItemsPerPagePending] = useState(10);

  const [currentPageAll, setCurrentPageAll] = useState(1);
  const [itemsPerPageAll, setItemsPerPageAll] = useState(10);

  const [approvalFilter, setApprovalFilter] = useState('All');
  const [searchEmailAll, setSearchEmailAll] = useState('');
  const [courseFilterAll, setCourseFilterAll] = useState('All');
  const [searchEmailPending, setSearchEmailPending] = useState('');
  const [courseFilterPending, setCourseFilterPending] = useState('All');
  const [uniqueCourses, setUniqueCourses] = useState(['All']);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    const fetchCertificates = async () => {
      try {
        setLoading(true);
        const data = await apiFetch(`${window._env_.API_BASE_URL}/admin-approvecert/certificates`);
        setOriginalCertificates(data || []);
        setOriginalPendingCertificates((data || []).filter(cert => cert.approval_status === 0));
        const courses = ['All', ...new Set((data || []).map(cert => cert.course_name || 'Unknown'))];
        setUniqueCourses(courses);
      } catch (err) {
        setError(err.message || 'Failed to fetch certificates');
      } finally {
        setLoading(false);
      }
    };
    fetchCertificates();
  }, []);

  // Filter All Certificates
  useEffect(() => {
    let result = [...originalCertificates];

    if (approvalFilter !== 'All') {
      result = result.filter(cert => {
        if (approvalFilter === 'Pending Approval') return cert.approval_status === 0;
        if (approvalFilter === 'Approved') return cert.approval_status === 1;
        if (approvalFilter === 'Rejected') return cert.approval_status === 2;
        return true;
      });
    }

    if (searchEmailAll) {
      result = result.filter(cert => (cert.email || '').toLowerCase().includes(searchEmailAll.toLowerCase()));
    }

    if (courseFilterAll !== 'All') {
      result = result.filter(cert => (cert.course_name || '') === courseFilterAll);
    }

    setFilteredCertificates(result);
  }, [originalCertificates, approvalFilter, searchEmailAll, courseFilterAll]);

  // Filter Pending Certificates
  useEffect(() => {
    let result = [...originalPendingCertificates];

    if (searchEmailPending) {
      result = result.filter(cert => (cert.email || '').toLowerCase().includes(searchEmailPending.toLowerCase()));
    }

    if (courseFilterPending !== 'All') {
      result = result.filter(cert => (cert.course_name || '') === courseFilterPending);
    }

    setFilteredPendingCertificates(result);
  }, [originalPendingCertificates, searchEmailPending, courseFilterPending]);

  // Export Data
  const getPendingExportData = () => {
    return filteredPendingCertificates.map(cert => ({
      "User Email": cert.email || 'N/A',
      "Course Name": cert.course_name || 'N/A',
      "Certificate Date": formatDate(cert.certificate_date),
      "Uploaded On": formatDate(cert.uploaded_at),
    }));
  };

  const getAllExportData = () => {
    return filteredCertificates.map(cert => ({
      "User Email": cert.email || 'N/A',
      "Course Name": cert.course_name || 'N/A',
      "Certificate Date": formatDate(cert.certificate_date),
      "Uploaded On": formatDate(cert.uploaded_at),
      "Status": cert.approval_status === 0 ? 'Pending' : cert.approval_status === 1 ? 'Approved' : 'Rejected',
      "Decision Date": cert.decision_date && cert.approval_status !== 0 ? formatDate(cert.decision_date) : 'N/A',
    }));
  };

  // Action Handlers (unchanged)
  const handleApprove = async (id) => {
    try {
      setActionLoading(`pending-approve-${id}`);
      await apiFetch(`${window._env_.API_BASE_URL}/admin-approvecert/approval/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approval_status: 1 }),
      });
      setOriginalCertificates(prev => prev.map(cert => cert.id === id ? { ...cert, approval_status: 1, decision_date: new Date().toISOString() } : cert));
      setOriginalPendingCertificates(prev => prev.filter(cert => cert.id !== id));
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id) => {
    try {
      setActionLoading(`pending-reject-${id}`);
      await apiFetch(`${window._env_.API_BASE_URL}/admin-approvecert/approval/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approval_status: 2 }),
      });
      setOriginalCertificates(prev => prev.map(cert => cert.id === id ? { ...cert, approval_status: 2, decision_date: new Date().toISOString() } : cert));
      setOriginalPendingCertificates(prev => prev.filter(cert => cert.id !== id));
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleView = async (id, table) => {
    try {
      setActionLoading(`${table}-view-${id}`);
      const blob = await apiFetchBlob(`${window._env_.API_BASE_URL}/external-certificates/view/${id}`);
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownload = async (id, table) => {
    try {
      setActionLoading(`${table}-download-${id}`);
      const blob = await apiFetchBlob(`${window._env_.API_BASE_URL}/external-certificates/download/${id}`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cert = originalCertificates.find(cert => cert.id === id);
      let fileExtension = cert?.file_type || 'pdf';

      // Clean up file extensions - remove any prefixes like 'image_', 'application_', etc.
      // and ensure we have proper extensions
      fileExtension = fileExtension.toLowerCase();

      // Handle common MIME type prefixes
      if (fileExtension.startsWith('image/')) {
        fileExtension = fileExtension.replace('image/', '');
      } else if (fileExtension.startsWith('application/')) {
        fileExtension = fileExtension.replace('application/', '');
      }

      // Map common MIME types to proper extensions
      const extensionMap = {
        'jpeg': 'jpg',
        'x-pdf': 'pdf',
        'pdf': 'pdf',
        'png': 'png',
        'jpg': 'jpg',
        'gif': 'gif',
        'webp': 'webp',
        'svg+xml': 'svg',
        'msword': 'doc',
        'vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
        'vnd.ms-excel': 'xls',
        'vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
        'zip': 'zip'
      };

      fileExtension = extensionMap[fileExtension] || fileExtension;

      // Ensure we have a valid extension, default to pdf if unknown
      if (!fileExtension || fileExtension.length > 10) {
        fileExtension = 'pdf';
      }

      a.download = `certificate_${id}.${fileExtension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const renderCourseName = (cert) => {
    const courseName = cert.course_name || 'N/A';
    if (cert.enrollment_url) {
      return <a href={cert.enrollment_url} target="_blank" rel="noopener noreferrer" className="aac-link">{courseName}</a>;
    }
    return courseName;
  };

  if (loading) {
    return (
      <div className="aac-loading-container">
        <div className="aac-loading-spinner"></div>
        <span className="aac-loading-text">Loading certificates...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="aac-error-container">
        <div className="aac-error-icon">Warning</div>
        <div className="aac-error-message">
          <h3>Something went wrong</h3>
          <p>{error}</p>
          <button className="aac-retry-btn" onClick={() => window.location.reload()}>Try Again</button>
        </div>
      </div>
    );
  }

  // Pagination slicing
  const pendingStart = (currentPagePending - 1) * itemsPerPagePending;
  const pendingEnd = currentPagePending * itemsPerPagePending;
  const currentItemsPending = filteredPendingCertificates.slice(pendingStart, pendingEnd);

  const allStart = (currentPageAll - 1) * itemsPerPageAll;
  const allEnd = currentPageAll * itemsPerPageAll;
  const currentItemsAll = filteredCertificates.slice(allStart, allEnd);

  return (
    <div className="aac-container">
      <div className="aac-header">
        <h1 className="aac-title">Approve Certifications</h1>
        <p className="aac-subtitle">Review and approve external certifications</p>
      </div>

      <div className="aac-cards-container">
        <div className="aac-card aac-card-warning">
          <div className="aac-card-icon"><FaClock /></div>
          <p className="aac-card-value">{filteredPendingCertificates.length}</p>
          <span className="aac-card-title">Pending Approvals</span>
        </div>
        <div className="aac-card aac-card-success">
          <div className="aac-card-icon"><FaUsers /></div>
          <p className="aac-card-value">
            {originalCertificates.filter(cert => cert.approval_status === 1).length}
          </p>
          <span className="aac-card-title">Approved</span>
        </div>
        <div className="aac-card aac-card-info">
          <div className="aac-card-icon"><FaCertificate /></div>
          <p className="aac-card-value">{originalCertificates.length}</p>
          <span className="aac-card-title">Total Certificates</span>
        </div>
      </div>

      {/* PENDING APPROVAL SECTION */}
      <div className="aac-section">
        <div className="aac-section-header">
          <h2 className="aac-section-title">
            <span className="aac-badge aac-badge-warning">{filteredPendingCertificates.length}</span>
            Pending Approval
          </h2>
        </div>

        <div className="aac-filters">
          <div className="aac-filter-group">
            <input type="text" placeholder="Search by email..." value={searchEmailPending} onChange={(e) => setSearchEmailPending(e.target.value)} className="aac-input" />
            <select value={courseFilterPending} onChange={(e) => setCourseFilterPending(e.target.value)} className="aac-select">
              {uniqueCourses.map(course => <option key={course} value={course}>{course}</option>)}
            </select>
            <ClearFilter onClear={() => { setSearchEmailPending(''); setCourseFilterPending('All'); }} />
            <ExportTable
              data={getPendingExportData()}
              headers={['User Email', 'Course Name', 'Certificate Date', 'Uploaded On']}
              tableName="Pending Certificates"
              className="ad-dashboard-export-btn"
            />
          </div>
        </div>

        <div className="aac-table-container">
          <table className="aac-table">
            <thead>
              <tr>
                <th>User Email</th>
                <th>Course Name</th>
                <th>Certificate Date</th>
                <th>Uploaded On</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItemsPending.length === 0 ? (
                <tr><td colSpan="5" className="aac-no-data">No pending certificates found</td></tr>
              ) : (
                currentItemsPending.map(cert => (
                  <tr key={cert.id}>
                    <td>{cert.email || 'N/A'}</td>
                    <td>{renderCourseName(cert)}</td>
                    <td>{formatDate(cert.certificate_date)}</td>
                    <td>{formatDate(cert.uploaded_at)}</td>
                    <td>
                      <div className="aac-actions">
                        <button onClick={() => handleApprove(cert.id)} className="aac-btn-icon aac-btn-approve" title="Approve">
                          {actionLoading === `pending-approve-${cert.id}` ? '...' : <FaCheck />}
                        </button>
                        <button onClick={() => handleReject(cert.id)} className="aac-btn-icon aac-btn-reject" title="Reject">
                          {actionLoading === `pending-reject-${cert.id}` ? '...' : <FaTimes />}
                        </button>
                        <button onClick={() => handleView(cert.id, 'pending')} className="aac-icon-btn" title="View">
                          {actionLoading === `pending-view-${cert.id}` ? '...' : <Eye size={20} />}
                        </button>
                        <button onClick={() => handleDownload(cert.id, 'pending')} className="aac-icon-btn" title="Download">
                          {actionLoading === `pending-download-${cert.id}` ? '...' : <Download size={20} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          items={filteredPendingCertificates}
          itemsPerPage={itemsPerPagePending}
          setItemsPerPage={setItemsPerPagePending}
          currentPage={currentPagePending}
          setCurrentPage={setCurrentPagePending}
        />
      </div>

      {/* ALL CERTIFICATES SECTION */}
      <div className="aac-section">
        <div className="aac-section-header">
          <h2 className="aac-section-title">
            <span className="aac-badge aac-badge-info">{filteredCertificates.length}</span>
            All Certificates
          </h2>
        </div>

        <div className="aac-filters">
          <div className="aac-filter-group">
            <input type="text" placeholder="Search by email..." value={searchEmailAll} onChange={(e) => setSearchEmailAll(e.target.value)} className="aac-input" />
            <select value={courseFilterAll} onChange={(e) => setCourseFilterAll(e.target.value)} className="aac-select">
              {uniqueCourses.map(course => <option key={course} value={course}>{course}</option>)}
            </select>
            <select value={approvalFilter} onChange={(e) => setApprovalFilter(e.target.value)} className="aac-select">
              <option value="All">All Status</option>
              <option value="Pending Approval">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </select>
            <ClearFilter onClear={() => { setSearchEmailAll(''); setCourseFilterAll('All'); setApprovalFilter('All'); }} />
            <ExportTable
              data={getAllExportData()}
              headers={['User Email', 'Course Name', 'Certificate Date', 'Uploaded On', 'Status', 'Decision Date']}
              tableName="All Certificates"
              className="ad-dashboard-export-btn"
            />
          </div>
        </div>

        <div className="aac-table-container">
          <table className="aac-table">
            <thead>
              <tr>
                <th>User Email</th>
                <th>Course Name</th>
                <th>Certificate Date</th>
                <th>Uploaded On</th>
                <th>Status</th>
                <th>Decision Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItemsAll.length === 0 ? (
                <tr><td colSpan="7" className="aac-no-data">No certificates found</td></tr>
              ) : (
                currentItemsAll.map(cert => (
                  <tr key={cert.id}>
                    <td>{cert.email || 'N/A'}</td>
                    <td>{renderCourseName(cert)}</td>
                    <td>{formatDate(cert.certificate_date)}</td>
                    <td>{formatDate(cert.uploaded_at)}</td>
                    <td>
                      <span className={`aac-status aac-status-${cert.approval_status === 0 ? 'pending' : cert.approval_status === 1 ? 'approved' : 'rejected'}`}>
                        {cert.approval_status === 0 ? 'Pending' : cert.approval_status === 1 ? 'Approved' : 'Rejected'}
                      </span>
                    </td>
                    <td>{cert.decision_date && cert.approval_status !== 0 ? formatDate(cert.decision_date) : 'N/A'}</td>
                    <td>
                      <div className="aac-actions">
                        <button onClick={() => handleView(cert.id, 'all')} className="aac-icon-btn" title="View">
                          {actionLoading === `all-view-${cert.id}` ? '...' : <Eye size={20} />}
                        </button>
                        <button onClick={() => handleDownload(cert.id, 'all')} className="aac-icon-btn" title="Download">
                          {actionLoading === `all-download-${cert.id}` ? '...' : <Download size={20} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          items={filteredCertificates}
          itemsPerPage={itemsPerPageAll}
          setItemsPerPage={setItemsPerPageAll}
          currentPage={currentPageAll}
          setCurrentPage={setCurrentPageAll}
        />
      </div>
    </div>
  );
};

export default ApproveCertificates;